import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import 'whatwg-fetch';
import { alertsManager } from './alerts';
import { MAX_UPLOAD_BYTES, resizeAndCompressImage } from '../lib/imageTransform';

const resizeImageUrl = (url, size = '100x100') => url.match(/^(http(s?):)?(\/\/)?([^\/]+)\.cloudfront.net/i)
    ? url.replace(/cloudfront.net\//i, `cloudfront.net/fit-in/${size}/`)
    : url;

/**
 * Surface an error to the merchant via the BigDesign alerts manager. fetch()
 * does not reject on HTTP 4xx/5xx, so every thunk must check resp.ok itself.
 * @param {string} header
 * @param {string|string[]} texts
 */
const notifyErrors = (header, texts) => {
    alertsManager.add({
        header,
        messages: (Array.isArray(texts) ? texts : [texts]).map(text => ({ text })),
        type: 'error',
        autoDismiss: true,
    });
};

/** Extract a human-readable message from a non-ok response (JSON or otherwise). */
const readError = async (resp) => {
    try {
        const json = await resp.json();
        if (json && json.error) {
            return typeof json.error === 'string' ? json.error : JSON.stringify(json.error);
        }
    } catch (e) {
        // non-JSON body
    }
    return `Request failed (${resp.status})`;
};

export const fetchReviews = createAsyncThunk('reviews/fetch', async (_arg, { rejectWithValue }) => {
    const params = new URLSearchParams(window.location.search);
    try {
        const resp = await fetch(`${process.env.API_URL}/reviews?${params.toString()}`);
        if (!resp.ok) {
            notifyErrors('Failed to load reviews', await readError(resp));
            return rejectWithValue(null);
        }
        const json = await resp.json();
        if (json && json.success) {
            return json;
        }
        return rejectWithValue(null);
    } catch (e) {
        console.error(e);
        notifyErrors('Failed to load reviews', e.message || 'Please try again.');
        return rejectWithValue(null);
    }
});

export const addImages = createAsyncThunk('reviewimages/upload', async ({ reviewId }, { getState, rejectWithValue }) => {
    const params = new URLSearchParams(window.location.search);
    const review = getState().reviews.data.filter(review => review.reviewId == reviewId)[0];
    const files = review ? review.files : [];

    const images = [];
    const uploadedNames = [];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
        const name = files[i].name;
        try {
            const blob = await resizeAndCompressImage(files[i], { maxDim: 1600, maxBytes: MAX_UPLOAD_BYTES });
            const formData = new FormData();
            formData.append('file', blob, name);

            const resp = await fetch(`${process.env.API_URL}/reviews/${reviewId}/images?${params.toString()}`, {
                method: 'POST',
                body: formData,
            });

            if (!resp.ok) {
                errors.push(`${name}: ${await readError(resp)}`);
                continue;
            }

            const json = await resp.json();
            if (!json || !json.data) {
                errors.push(`${name}: Upload failed`);
                continue;
            }
            images.push(json.data);
            uploadedNames.push(name);
        } catch (e) {
            console.error(e);
            errors.push(`${name}: ${e.message || 'Upload failed'}`);
        }
    }

    if (errors.length > 0) {
        notifyErrors('Some images were not uploaded', errors);
    }

    if (images.length === 0 && errors.length > 0) {
        return rejectWithValue({ errors });
    }

    return { images, uploadedNames };
});

export const deleteImage = createAsyncThunk('reviewimages/delete', async ({ reviewId, imageId }, { rejectWithValue }) => {
    const params = new URLSearchParams(window.location.search);
    try {
        const resp = await fetch(`${process.env.API_URL}/reviews/${reviewId}/images/${imageId}?${params.toString()}`, {
            method: 'DELETE',
        });
        if (!resp.ok) {
            notifyErrors('Failed to delete image', await readError(resp));
            return rejectWithValue(null);
        }
        const json = await resp.json();
        return json.data;
    } catch (e) {
        console.error(e);
        notifyErrors('Failed to delete image', e.message || 'Please try again.');
        return rejectWithValue(null);
    }
});

export const deleteReview = createAsyncThunk('reviews/delete', async (reviewId, { rejectWithValue }) => {
    const params = new URLSearchParams(window.location.search);
    try {
        const resp = await fetch(`${process.env.API_URL}/reviews/${reviewId}?${params.toString()}`, {
            method: 'DELETE',
        });
        if (!resp.ok) {
            notifyErrors('Failed to delete review', await readError(resp));
            return rejectWithValue(null);
        }
        const json = await resp.json();
        return json.data;
    } catch (e) {
        console.error(e);
        notifyErrors('Failed to delete review', e.message || 'Please try again.');
        return rejectWithValue(null);
    }
});

export const updateReview = createAsyncThunk('reviews/update', async (review, { rejectWithValue }) => {
    const params = new URLSearchParams(window.location.search);
    try {
        const formData = new FormData();
        Object.entries(review).forEach(([key, value]) => {
            // JSON.stringify objects like criteriaRatings
            const processedValue = typeof value === 'object' && value !== null
                ? JSON.stringify(value)
                : value;
            formData.append(key, processedValue);
        });

        const resp = await fetch(`${process.env.API_URL}/reviews/${review.reviewId}?${params.toString()}`, {
            method: 'PUT',
            body: formData,
        });
        if (!resp.ok) {
            notifyErrors('Failed to update review', await readError(resp));
            return rejectWithValue(null);
        }
        const json = await resp.json();
        return json.data;
    } catch (e) {
        console.error(e);
        notifyErrors('Failed to update review', e.message || 'Please try again.');
        return rejectWithValue(null);
    }
});

export const addReview = createAsyncThunk('reviews/add', async (review, { rejectWithValue }) => {
    const params = new URLSearchParams(window.location.search);
    try {
        const formData = new FormData();
        Object.entries(review).forEach(([key, value]) => {
            // JSON.stringify objects like criteriaRatings
            const processedValue = typeof value === 'object' && value !== null
                ? JSON.stringify(value)
                : value;
            formData.append(key, processedValue);
        });

        const resp = await fetch(`${process.env.API_URL}/reviews?${params.toString()}`, {
            method: 'POST',
            body: formData,
        });
        if (!resp.ok) {
            notifyErrors('Failed to add review', await readError(resp));
            return rejectWithValue(null);
        }
        const json = await resp.json();
        return json.data;
    } catch (e) {
        console.error(e);
        notifyErrors('Failed to add review', e.message || 'Please try again.');
        return rejectWithValue(null);
    }
});



const reviewsSlice = createSlice({
    name: 'reviews',
    initialState: {
        loading: 'idle',
        data: [],
        meta: {},
    },
    reducers: {
        addFiles: (state, action) => {
            const { reviewId, files } = action.payload;
            const review = state.data.filter(review => review.reviewId === reviewId)[0];
            if (review) {
                review.files = review.files ? [...review.files, ...files] : files;
            }
        },
        setFiles: (state, action) => {
            const { reviewId, files } = action.payload;
            const review = state.data.filter(review => review.reviewId === reviewId)[0];
            if (review) {
                review.files = files;
            }
        },
        deleteFileByName: (state, action) => {
            const { reviewId, name } = action.payload;
            const review = state.data.filter(review => review.reviewId === reviewId)[0];
            if (review) {
                review.files = review.files.filter(file => file.name !== name);
            }
        }
    },
    extraReducers: {
        [fetchReviews.pending]: (state) => {
            state.loading = 'pending';
        },
        [fetchReviews.fulfilled]: (state, action) => {
            state.loading = 'idle';
            const payload = action.payload || {};
            state.data = payload.data || [];
            state.data.forEach(review => {
                review.images.forEach(image => {
                    image.thumbUrl = resizeImageUrl(image.fileUrl, '100x100');
                    image.smallUrl = resizeImageUrl(image.fileUrl, '500x500');
                })
                if (!review.files) {
                    review.files = [];
                }
            });
            state.meta = payload.meta || {};
        },
        [fetchReviews.rejected]: (state) => {
            state.loading = 'idle';
        },
        [addImages.pending]: (state) => {
            state.loading = 'pending';
        },
        [addImages.fulfilled]: (state, action) => {
            state.loading = 'idle';
            const review = state.data.filter(review => review.reviewId === action.meta.arg.reviewId)[0];
            if (review) {
                const { images = [], uploadedNames = [] } = action.payload || {};
                images.forEach(image => {
                    image.thumbUrl = resizeImageUrl(image.fileUrl, '100x100');
                    image.smallUrl = resizeImageUrl(image.fileUrl, '500x500');
                });
                review.images = [...review.images, ...images];
                // Drop only the files that uploaded successfully; keep failed ones
                // so the merchant can retry (matched by name, like deleteFileByName).
                review.files = review.files.filter(file => !uploadedNames.includes(file.name));
            }
        },
        [addImages.rejected]: (state) => {
            // Keep review.files so the merchant can retry the failed uploads.
            state.loading = 'idle';
        },
        [deleteImage.pending]: (state) => {
            state.loading = 'pending';
        },
        [deleteImage.fulfilled]: (state, action) => {
            state.loading = 'idle';
            const review = state.data.filter(review => review.reviewId === action.meta.arg.reviewId)[0];
            if (review) {
                review.images = review.images.filter(image => image._id !== action.meta.arg.imageId);
            }
        },
        [deleteImage.rejected]: (state) => {
            state.loading = 'idle';
        },
        [deleteReview.pending]: (state) => {
            state.loading = 'pending';
        },
        [deleteReview.fulfilled]: (state, action) => {
            state.loading = 'idle';
            state.data = state.data.filter(review => review.reviewId !== action.meta.arg);
        },
        [deleteReview.rejected]: (state) => {
            // Do NOT remove the review from the UI when the server rejected the delete.
            state.loading = 'idle';
        },
        [updateReview.pending]: (state, action) => {
            state.loading = 'pending';
            state.data.forEach(review => {
                if (review.reviewId === action.meta.arg.reviewId) {
                    review.status_tmp = review.status;
                    review.status = action.meta.arg.status;
                }
            });
        },
        [updateReview.fulfilled]: (state, action) => {
            state.loading = 'idle';
            state.data.forEach(review => {
                if (review.reviewId === action.meta.arg.reviewId) {
                    review.status = review.status_tmp;
                    delete review.status_tmp;
                }
                if (action.payload && review.reviewId === action.payload.reviewId) {
                    const { images, ...updatedData } = action.payload;
                    Object.assign(review, updatedData);
                }
            });
        },
        [updateReview.rejected]: (state, action) => {
            // Roll back the optimistic status change applied in pending.
            state.loading = 'idle';
            state.data.forEach(review => {
                if (review.reviewId === action.meta.arg.reviewId && 'status_tmp' in review) {
                    review.status = review.status_tmp;
                    delete review.status_tmp;
                }
            });
        },
        [addReview.pending]: (state, action) => {
            state.loading = 'pending';
        },
        [addReview.fulfilled]: (state, action) => {
            state.loading = 'idle';
            if (action.payload) {
                state.data.unshift(action.payload);
            }
        },
        [addReview.rejected]: (state) => {
            state.loading = 'idle';
        },
    }
});

export const { addFiles, setFiles, deleteFileByName } = reviewsSlice.actions;

export default reviewsSlice.reducer;
